import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLE_KEYS, type Role } from '@siderproj/core';
import { api, type Member } from '../api';
import { useMe } from '../auth';
import { keys, useMembers, usePerm, useProjectId, EXTERNAL_ROLES } from '../data';
import { PageHead } from '../layout';
import { Btn, Badge, DataTable, ErrorBox, Field, Icon, Loading, Modal, ReasonDialog, Select, useToast } from '../ui/kit';
import { fmtDate, ROLE_LABEL } from '../format';

const roleOptions = ROLE_KEYS.map(r => ({ value: r, label: ROLE_LABEL[r] ?? r }));

export function TeamPage() {
  const id = useProjectId(); const me = useMe(); const perm = usePerm(id); const toast = useToast(); const qc = useQueryClient();
  const q = useMembers(id); const manage = perm.can('membros', 'approve');
  const [adding, setAdding] = useState(false); const [revoke, setRevoke] = useState<Member | null>(null); const [showOld, setShowOld] = useState(false);
  const rows = (q.data ?? []).filter(m => showOld || !m.revokedAt);
  const changeRole = async (m: Member, role: Role) => {
    try { await api.patch(`/api/projects/${id}/members/${m.id}`, { role }); toast.ok('Perfil alterado.'); await qc.invalidateQueries({ queryKey: keys.members(id) }); }
    catch (e) { toast.err(e); }
  };
  const setRights = async (m: Member, patch: Partial<Pick<Member, 'canView' | 'canDownload' | 'canComment' | 'canApprove'>>) => {
    try { await api.patch(`/api/projects/${id}/members/${m.id}`, patch); toast.ok('Permissões atualizadas. A mudança vale a partir do próximo acesso.'); await qc.invalidateQueries({ queryKey: keys.members(id) }); }
    catch (e) { toast.err(e); }
  };
  return (
    <>
      <PageHead eyebrow="Projeto" title="Equipe" sub="Quem participa do projeto e com qual perfil. O perfil define o que cada pessoa vê e altera.">
        <label className="check small"><input type="checkbox" checked={showOld} onChange={e => setShowOld(e.target.checked)} />Mostrar acessos revogados</label>
        {manage && <button className="btn primary" onClick={() => setAdding(true)}><Icon name="plus" />Incluir pessoa</button>}
      </PageHead>
      <div className="panel">
        {q.isLoading ? <Loading /> : q.error ? <div className="panel-body"><ErrorBox error={q.error} /></div> : (
          <DataTable rows={rows} search={m => `${m.name} ${m.email} ${ROLE_LABEL[m.role] ?? ''}`} initialSort="name"
            cols={[
              { key: 'name', label: 'Nome', sort: m => m.name, render: m => <>{m.name}{m.userId === me.id && <span className="faint small"> (você)</span>}</> },
              { key: 'email', label: 'E-mail', sort: m => m.email },
              { key: 'role', label: 'Perfil', sort: m => ROLE_LABEL[m.role] ?? m.role, render: m => manage && !m.revokedAt
                ? <Select className="sm" aria-label={`Perfil de ${m.name}`} value={m.role as Role} onChange={r => r && r !== m.role && void changeRole(m, r)} options={roleOptions} />
                : ROLE_LABEL[m.role] ?? m.role },
              { key: 'docs', label: 'Documentos (externos)', render: m => EXTERNAL_ROLES.includes(m.role) ? <Rights m={m} editable={manage && !m.revokedAt} onChange={patch => void setRights(m, patch)} /> : <span className="small faint">conforme o perfil</span> },
              { key: 'since', label: 'Desde', sort: m => m.since, render: m => fmtDate(m.since) },
              { key: 'st', label: 'Situação', render: m => m.revokedAt ? <Badge tone="crit">Revogado</Badge> : <Badge tone="ok">Ativo</Badge> },
              { key: 'act', label: '', render: m => manage && !m.revokedAt && m.userId !== me.id ? <button className="btn sm danger" onClick={() => setRevoke(m)}>Revogar</button> : null }
            ]} />
        )}
      </div>
      {adding && <AddMember projectId={id} onClose={() => setAdding(false)} />}
      {revoke && <ReasonDialog title={`Revogar acesso de ${revoke.name}`} confirm="Revogar acesso" danger onClose={() => setRevoke(null)}
        onConfirm={async reason => { await api.patch(`/api/projects/${id}/members/${revoke.id}`, { revoke: true, reason }); toast.ok('Acesso revogado.'); await qc.invalidateQueries({ queryKey: keys.members(id) }); }}>
        <p className="muted">A pessoa deixa de ver o projeto imediatamente. O histórico do que ela fez continua preservado.</p>
      </ReasonDialog>}
    </>
  );
}

const RIGHTS = [['canView', 'Ver'], ['canDownload', 'Baixar'], ['canComment', 'Comentar'], ['canApprove', 'Aprovar']] as const;
function Rights({ m, editable, onChange }: { m: Pick<Member, 'canView' | 'canDownload' | 'canComment' | 'canApprove'>; editable: boolean; onChange: (p: Partial<Member>) => void }) {
  return (
    <div className="row" style={{ gap: 10, flexWrap: 'nowrap' }} onClick={e => e.stopPropagation()}>
      {RIGHTS.map(([k, label]) => (
        <label key={k} className="check small" title={k === 'canView' ? 'Sem "Ver", a pessoa não acessa nenhum documento' : undefined}>
          <input type="checkbox" checked={m[k]} disabled={!editable || (k !== 'canView' && !m.canView)} onChange={e => onChange({ [k]: e.target.checked })} />{label}
        </label>
      ))}
    </div>
  );
}

function AddMember({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const toast = useToast(); const qc = useQueryClient();
  const cands = useQuery({ queryKey: ['project', projectId, 'candidates'], queryFn: () => api.get<{ id: string; name: string; email: string; organizationName: string }[]>(`/api/projects/${projectId}/candidates`) });
  const members = useMembers(projectId);
  const [userId, setUserId] = useState<string | null>(null); const [role, setRole] = useState<Role | null>('eng_disc'); const [busy, setBusy] = useState(false);
  const [rights, setRightsState] = useState({ canView: true, canDownload: true, canComment: true, canApprove: false });
  const active = new Set((members.data ?? []).filter(m => !m.revokedAt).map(m => m.userId));
  const options = (cands.data ?? []).filter(c => !active.has(c.id)).map(c => ({ value: c.id, label: `${c.name} · ${c.organizationName}` }));
  const save = async () => {
    if (!userId || !role) return;
    setBusy(true);
    try { await api.post(`/api/projects/${projectId}/members`, { userId, role, ...(EXTERNAL_ROLES.includes(role) ? rights : {}) }); toast.ok('Pessoa incluída.'); await qc.invalidateQueries({ queryKey: keys.members(projectId) }); onClose(); }
    catch (e) { toast.err(e); } finally { setBusy(false); }
  };
  return (
    <Modal title="Incluir pessoa" size="narrow" onClose={onClose} foot={<><button className="btn" onClick={onClose}>Cancelar</button><Btn className="btn primary" why={busy ? 'Aguarde: gravando.' : !userId ? 'Escolha a pessoa.' : !role && 'Escolha o perfil.'} onClick={save}>Incluir</Btn></>}>
      {cands.isLoading ? <Loading /> : cands.error ? <ErrorBox error={cands.error} /> : (
        <div className="stack">
          <Field label="Pessoa" required help="Aparecem usuários ativos da empresa dona e das empresas participantes do projeto.">
            <Select value={userId} onChange={setUserId} empty={options.length ? 'Selecione' : 'Ninguém disponível'} options={options} />
          </Field>
          <Field label="Perfil" required help={role === 'cliente' ? 'Cliente vê só o resumo do projeto e os documentos emitidos para a empresa dele.' : role === 'gerente' || role === 'diretor' || role === 'diretor_fin' ? 'Dá acesso ao financeiro (custos, medições e valores). Só para pessoas da VMM.' : undefined}><Select value={role} onChange={setRole} options={roleOptions} /></Field>
          {role && EXTERNAL_ROLES.includes(role) && <Field label="Permissões nos documentos" group><Rights m={rights} editable onChange={p => setRightsState(o => ({ ...o, ...p }))} /></Field>}
        </div>
      )}
    </Modal>
  );
}
